import Link from "next/link";
import { VFC, useState, useEffect, memo } from "react";

import { notifBody } from "lib/createNotif";

import { CurrentUser, Notif, NotifType } from "lib/type";

import { IoMdNotifications } from "react-icons/io";
import { VscChromeClose } from "react-icons/vsc";
import { FiClock } from "react-icons/fi";
import { FaThumbsUp, FaComments } from "react-icons/fa";
import { RiUserAddFill } from "react-icons/ri";
import { MdAnnouncement } from "react-icons/md";
import { IoClose } from "react-icons/io5";

const notifHref = (type: NotifType, articleID = "", notifUid = "") => {
  switch (type) {
    case "like":
      return `/articles/${articleID}`;
    case "comment":
      return `/articles/${articleID}`;
    case "follow":
      return `/users/${notifUid}`;
    default:
      return "";
  }
};

const notifIcon = (type: NotifType) => {
  switch (type) {
    case "like":
      return <FaThumbsUp />;
    case "comment":
      return <FaComments />;
    case "follow":
      return <RiUserAddFill />;
    case "announce":
      return <MdAnnouncement />;
    default:
      return "";
  }
};

type NotifProps = {
  notif: boolean;
  notifs: Notif[];
  user: CurrentUser;
  closeMenu: (type: "side" | "notif" | "search") => void;
};

const NotifList: VFC<NotifProps> = ({ notif, notifs, closeMenu }) => {
  const notifList = notifs
    .map((notif) => {
      const {
        notifID,
        type,
        articleID,
        notifUid,
        userName,
        articleTitle,
        announce,
        createdAt,
      } = notif;
      if (!notifID) return;

      return (
        <li key={notifID} className="relative overflow-hidden">
          <Link href={notifHref(type, articleID, notifUid)} prefetch={false}>
            <a className="border-light-800 block border-b-2 border-dotted pb-2">
              <span className="absolute top-1/2 right-0 -translate-y-1/2 transform text-6xl opacity-20">
                {notifIcon(type)}
              </span>
              <p className="mb-2 leading-6">
                {notifBody(type, userName, articleTitle, announce)}
              </p>
              <p className="flex items-center text-sm opacity-80">
                <FiClock />
                {createdAt}
              </p>
            </a>
          </Link>
        </li>
      );
    })
    .filter((notif) => notif);

  const [isAnimated, setIsAnimated] = useState(true);
  useEffect(() => {
    if (notif) setIsAnimated(false);
  }, [notif]);
  const closeNotifs = () => {
    closeMenu("notif");
    setTimeout(() => setIsAnimated(true), 500);
  };

  const lgCloseNotif = ({
    target,
    currentTarget,
  }: {
    target: EventTarget;
    currentTarget: EventTarget;
  }) => {
    if (target === currentTarget) closeNotifs();
  };

  return (
    <div
      className={`notif fixed left-0 ${
        notif ? "top-0 lg:opacity-100" : "top-full lg:opacity-0"
      } lg:bg-dark lg:dark:bg-light z-20 h-screen w-screen lg:bg-opacity-10 lg:backdrop-blur-sm lg:dark:bg-opacity-10
      ${isAnimated ? "lg:top-full" : "lg:top-0"} 
      `}
      onClick={lgCloseNotif}
    >
      <div
        className={`fixed right-0 ${
          notif ? "top-0 lg:right-0" : "top-full lg:-right-full"
        } bg-light dark:bg-dark mb-4 h-full w-full bg-opacity-90 backdrop-blur-sm dark:bg-opacity-90
        lg:top-0 lg:mr-0 lg:ml-auto lg:mb-0 lg:flex lg:w-1/4 lg:flex-col lg:bg-opacity-100 lg:dark:bg-opacity-100`}
      >
        <div className="bg-light-50 dark:bg-dark-600 bg-opacity-40 py-2 dark:bg-opacity-40">
          <div className="container flex items-center">
            <p className="mr-auto flex items-center text-xl">
              <IoMdNotifications />
              おしらせ
            </p>
            <button onClick={closeNotifs} className="lg:hidden">
              <VscChromeClose className="mr-1" />
              閉じる
            </button>
          </div>
        </div>
        <div className="h-[95%] overflow-y-auto lg:h-full">
          {notifs.length < 1 ? (
            <p className="container py-4">お知らせはありません。</p>
          ) : (
            <ul className="container flex flex-col space-y-4 py-4">
              {notifList}
            </ul>
          )}
        </div>
      </div>
      <div className="bg-dark text-light dark:bg-light dark:text-dark pointer-events-none absolute top-4 left-4 hidden rounded-full p-1 lg:block">
        <IoClose />
      </div>
    </div>
  );
};

export default memo(NotifList);
